import React, { useState, useEffect } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { withRouter, RouteComponentProps, useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { withStyles, CircularProgress, Typography, Box } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import Dashboard from '../../../../layouts/Dashboard';
import EditLocationPrinter from '../../../../components/Global/LocationPrinters/Edit';

import { editLocationPrinterInitQuery, LocationPrinter } from './queries';

import styles from './styles';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
}

interface UrlParams {
  printer_id: string;
}

const LocEditLocationPrinter = ({ classes, history }: Props): React.ReactElement => {
  const { printer_id } = useParams<UrlParams>();
  const { enqueueSnackbar } = useSnackbar();

  const [printer, setPrinter] = useState<LocationPrinter>({
    name: '',
    location: {
      name: '',
      organisation: {
        menus: [],
      },
      devices: [],
    },
  });

  const { data: editLocationPrinterInitQueryData, loading, error } = useQuery(editLocationPrinterInitQuery(printer_id), { fetchPolicy: 'no-cache' });

  useEffect(() => {
    let mounted = true;
    if (mounted && editLocationPrinterInitQueryData && editLocationPrinterInitQueryData.location_devices_by_pk) {
      setPrinter(editLocationPrinterInitQueryData.location_devices_by_pk);
    }
    return () => {
      mounted = false;
    };
  }, [editLocationPrinterInitQueryData]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Failed to load printer data. Please try again.', { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const handleFinish = () => {
    history.push(`/loc/printers`);
  };

  if (loading) {
    return (
      <Dashboard breadcrumbs={[{ label: 'Printers', link: `/loc/printers` }, { label: 'Edit' }]}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Dashboard>
    );
  }

  if (error) {
    return (
      <Dashboard breadcrumbs={[{ label: 'Printers', link: `/loc/printers` }, { label: 'Edit' }]}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography color="error">Error loading printer: {error.message}</Typography>
        </Box>
      </Dashboard>
    );
  }

  if (!editLocationPrinterInitQueryData || !editLocationPrinterInitQueryData.location_devices_by_pk) {
    return (
      <Dashboard breadcrumbs={[{ label: 'Printers', link: `/loc/printers` }, { label: 'Edit' }]}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Printer not found or you don't have access to it.</Typography>
        </Box>
      </Dashboard>
    );
  }

  return (
    <>
      <Dashboard breadcrumbs={[{ label: 'Printers', link: `/loc/printers` }, { label: printer.name }, { label: 'Edit' }]}>
        <EditLocationPrinter
          printer_id={printer_id}
          locations={printer.location?.devices?.map((i) => i.device_config?.location).filter(Boolean) || []}
          menus={printer.location?.organisation?.menus || []}
          handleFinish={handleFinish}
        />
      </Dashboard>
    </>
  );
};

export default withRouter(withStyles(styles)(LocEditLocationPrinter));
